import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PLESK_FLAG_DEFS,
  PLESK_ITEM_KINDS,
  type PleskFlagSeverity,
  type PleskImportStatusDto,
  type PleskInventoryItemDto,
  type PleskItemKind,
  type PleskReadinessReport,
  type PleskSettingsDto,
  type PleskSnapshotDetailDto,
  type PleskSnapshotDto,
  type PleskTestResultDto,
} from '@cpm/shared';
import { useEffect, useState } from 'react';
import { useMe } from '../api/auth.js';
import { api, ApiClientError } from '../api/client.js';
import { ImportRunsCard, ImportWizardCard } from '../components/plesk-import-wizard.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  type BadgeTone,
  cx,
} from '../components/ui.js';

/**
 * Plesk Import (G8): connect the legacy Plesk server (read-only), run
 * inventory snapshots, read the migration-readiness report, export it.
 */

function usePleskStatus() {
  return useQuery({
    queryKey: ['plesk-import', 'status'],
    queryFn: () => api.get<PleskImportStatusDto>('/api/plesk-import/status'),
    retry: false,
    // Poll while a snapshot is running so the page updates itself.
    refetchInterval: (query) => (query.state.data?.running ? 2000 : false),
  });
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function snapshotTone(status: PleskSnapshotDto['status']): BadgeTone {
  if (status === 'completed') return 'green';
  if (status === 'partial') return 'yellow';
  if (status === 'failed') return 'red';
  return 'blue'; // running
}

const SEVERITY_TONE: Record<PleskFlagSeverity, BadgeTone> = {
  high: 'red',
  warn: 'yellow',
  info: 'blue',
};

// ── Connection settings (admin) ───────────────────────────────────────

function ConnectionCard() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['plesk-import', 'settings'],
    queryFn: () => api.get<PleskSettingsDto>('/api/plesk-import/settings'),
    retry: false,
  });

  const [host, setHost] = useState('');
  const [port, setPort] = useState(8443);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [verifyTls, setVerifyTls] = useState(true);
  const [caCertificate, setCaCertificate] = useState('');
  const [sshEnabled, setSshEnabled] = useState(false);
  const [sshHost, setSshHost] = useState('');
  const [sshPort, setSshPort] = useState(22);
  const [sshUsername, setSshUsername] = useState('root');
  const [sshPrivateKey, setSshPrivateKey] = useState('');
  const [testResult, setTestResult] = useState<PleskTestResultDto | null>(null);

  useEffect(() => {
    const d = settings.data;
    if (!d) return;
    if (d.host) setHost(d.host);
    setPort(d.port);
    if (d.login) setLogin(d.login);
    setVerifyTls(d.verifyTls);
    setCaCertificate(d.caCertificate ?? '');
    setSshEnabled(d.ssh.enabled);
    setSshHost(d.ssh.host ?? '');
    setSshPort(d.ssh.port);
    setSshUsername(d.ssh.username);
  }, [settings.data]);

  const payload = () => ({
    host,
    port,
    login,
    password: password || undefined,
    verifyTls,
    caCertificate: caCertificate.trim() || null,
    ssh: sshEnabled
      ? {
          enabled: true,
          host: sshHost.trim() || undefined,
          port: sshPort,
          username: sshUsername,
          privateKey: sshPrivateKey || undefined,
        }
      : { enabled: false, port: 22, username: sshUsername || 'root' },
  });

  const test = useMutation({
    mutationFn: () => api.post<PleskTestResultDto>('/api/plesk-import/test', payload()),
    onSuccess: setTestResult,
  });

  const save = useMutation({
    mutationFn: () => api.put('/api/plesk-import/settings', payload()),
    onSuccess: () => {
      setPassword('');
      setSshPrivateKey('');
      setTestResult(null);
      void queryClient.invalidateQueries({ queryKey: ['plesk-import'] });
    },
  });

  // 403 = staff; the card is admin-only by construction.
  if (settings.error) return null;

  const checkboxCls =
    'size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-800';

  return (
    <Card>
      <CardHeader
        title="Plesk connection"
        description="Administrator credentials for the legacy Plesk server. The panel only ever reads — the connector refuses write operations by construction."
        actions={
          settings.data?.configured ? (
            <Badge tone="green">Connected</Badge>
          ) : (
            <Badge tone="yellow">Not configured</Badge>
          )
        }
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="space-y-4 px-5 py-4"
      >
        <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
          <div>
            <Label htmlFor="plesk-host">Hostname or IP</Label>
            <Input
              id="plesk-host"
              placeholder="plesk.example.com"
              required
              value={host}
              onChange={(e) => setHost(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="plesk-port">Port</Label>
            <Input
              id="plesk-port"
              type="number"
              value={port}
              onChange={(e) => setPort(Number(e.target.value))}
            />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="plesk-login">Administrator login</Label>
            <Input
              id="plesk-login"
              placeholder="admin"
              required
              value={login}
              onChange={(e) => setLogin(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="plesk-password">Password</Label>
            <Input
              id="plesk-password"
              type="password"
              placeholder={
                settings.data?.hasPassword ? '•••••••• (leave blank to keep current)' : 'Password'
              }
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Stored encrypted (AES-256-GCM). Never displayed again after saving.
            </p>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            className={checkboxCls}
            checked={verifyTls}
            onChange={(e) => setVerifyTls(e.target.checked)}
          />
          Verify the panel&apos;s TLS certificate
        </label>
        {!verifyTls && (
          <p className="rounded-lg bg-amber-50 px-4 py-2 text-xs text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300">
            Verification disabled — the connection is open to interception. Prefer pinning the
            panel&apos;s CA certificate below.
          </p>
        )}
        <div>
          <Label htmlFor="plesk-ca">CA certificate (PEM, optional)</Label>
          <Textarea
            id="plesk-ca"
            rows={3}
            placeholder="-----BEGIN CERTIFICATE----- (pin a self-signed panel's CA instead of disabling verification)"
            value={caCertificate}
            onChange={(e) => setCaCertificate(e.target.value)}
          />
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-800">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              className={checkboxCls}
              checked={sshEnabled}
              onChange={(e) => setSshEnabled(e.target.checked)}
            />
            Collect migration evidence over SSH
          </label>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Fixed read-only collectors add cron jobs, database sizes, Git deployment metadata and
            runtime/toolkit usage. Arbitrary commands are never accepted.
          </p>
          {sshEnabled && (
            <div className="mt-3 space-y-4">
              <div className="grid gap-4 sm:grid-cols-[1fr_6rem_10rem]">
                <div>
                  <Label htmlFor="plesk-ssh-host">SSH host</Label>
                  <Input
                    id="plesk-ssh-host"
                    placeholder={host || 'defaults to the panel host'}
                    value={sshHost}
                    onChange={(e) => setSshHost(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="plesk-ssh-port">Port</Label>
                  <Input
                    id="plesk-ssh-port"
                    type="number"
                    value={sshPort}
                    onChange={(e) => setSshPort(Number(e.target.value))}
                  />
                </div>
                <div>
                  <Label htmlFor="plesk-ssh-user">User</Label>
                  <Input
                    id="plesk-ssh-user"
                    value={sshUsername}
                    onChange={(e) => setSshUsername(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="plesk-ssh-key">Private key</Label>
                <Textarea
                  id="plesk-ssh-key"
                  rows={4}
                  placeholder={
                    settings.data?.ssh.hasKey
                      ? '•••••••• (leave blank to keep current)'
                      : '-----BEGIN OPENSSH PRIVATE KEY-----'
                  }
                  value={sshPrivateKey}
                  onChange={(e) => setSshPrivateKey(e.target.value)}
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Stored encrypted (AES-256-GCM). Never displayed again after saving.
                </p>
              </div>
            </div>
          )}
        </div>

        {testResult && (
          <div className="space-y-1 rounded-lg bg-slate-50 px-4 py-3 text-sm ring-1 ring-inset ring-slate-200 dark:bg-slate-800/50 dark:ring-slate-700">
            <TestLine label="XML API" ok={testResult.xmlApi.ok} error={testResult.xmlApi.error} />
            <TestLine
              label="REST API"
              ok={testResult.restApi.ok}
              error={testResult.restApi.error}
              extra={testResult.restApi.version ? `Plesk ${testResult.restApi.version}` : undefined}
            />
            {testResult.ssh && (
              <TestLine label="SSH" ok={testResult.ssh.ok} error={testResult.ssh.error} />
            )}
          </div>
        )}
        {(test.error ?? save.error) != null && <ErrorNote error={test.error ?? save.error} />}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            loading={test.isPending}
            onClick={() => test.mutate()}
            disabled={!host || !login || (!password && !settings.data?.hasPassword)}
          >
            Test connection
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!host || !login}>
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}

function TestLine({
  label,
  ok,
  error,
  extra,
}: {
  label: string;
  ok: boolean;
  error: string | null;
  extra?: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <Badge tone={ok ? 'green' : 'red'}>{label}</Badge>
      <span className="text-slate-600 dark:text-slate-300">
        {ok ? (extra ?? 'OK') : (error ?? 'failed')}
      </span>
    </div>
  );
}

// ── Snapshots ─────────────────────────────────────────────────────────

function SnapshotsCard({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const status = usePleskStatus();
  const snapshots = useQuery({
    queryKey: ['plesk-import', 'snapshots'],
    queryFn: () => api.get<PleskSnapshotDto[]>('/api/plesk-import/snapshots'),
    refetchInterval: () => (status.data?.running ? 2000 : false),
  });
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';

  const run = useMutation({
    mutationFn: () => api.post('/api/plesk-import/snapshots'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['plesk-import'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/plesk-import/snapshots/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['plesk-import'] }),
  });

  return (
    <Card>
      <CardHeader
        title="Inventory snapshots"
        description="Each run records a frozen, versioned inventory of everything the Plesk server holds."
        actions={
          <Button
            loading={run.isPending || status.data?.running}
            disabled={!status.data?.configured}
            onClick={() => run.mutate()}
          >
            Run snapshot
          </Button>
        }
      />
      {(run.error ?? remove.error) != null && (
        <div className="px-5 pt-3">
          <ErrorNote error={run.error ?? remove.error} />
        </div>
      )}
      {snapshots.isPending ? (
        <Spinner />
      ) : snapshots.error ? (
        <div className="p-5">
          <ErrorNote error={snapshots.error} />
        </div>
      ) : snapshots.data.length === 0 ? (
        <EmptyState
          title="No snapshots yet"
          description={
            status.data?.configured
              ? 'Run the first snapshot to inventory the Plesk server.'
              : 'Configure the Plesk connection first.'
          }
        />
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {snapshots.data.map((snap) => (
            <li
              key={snap.id}
              className={cx(
                'flex cursor-pointer items-center justify-between gap-3 px-5 py-3 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50',
                snap.id === selectedId && 'bg-brand-50/50 dark:bg-brand-500/10',
              )}
              onClick={() => onSelect(snap.id)}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm">
                  <Badge tone={snapshotTone(snap.status)}>{snap.status}</Badge>
                  <span className="font-medium text-slate-700 dark:text-slate-200">
                    {new Date(snap.startedAt).toLocaleString()}
                  </span>
                  {snap.pleskVersion && (
                    <span className="text-xs text-slate-500">Plesk {snap.pleskVersion}</span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                  {snap.stats
                    ? `${snap.stats.counts.subscription ?? 0} subscriptions · ${snap.stats.counts.domain ?? 0} domains · ${snap.stats.counts.mailbox ?? 0} mailboxes · ${snap.stats.counts.database ?? 0} databases`
                    : (snap.error ?? 'running…')}
                  {snap.warnings.length > 0 && ` · ${snap.warnings.length} warning(s)`}
                </p>
              </div>
              {isAdmin && snap.status !== 'running' && (
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove.mutate(snap.id);
                  }}
                >
                  Delete
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ── Readiness report ──────────────────────────────────────────────────

function ReportCard({ snapshotId }: { snapshotId: string | null }) {
  const detail = useQuery({
    queryKey: ['plesk-import', 'snapshot', snapshotId],
    queryFn: () => api.get<PleskSnapshotDetailDto>(`/api/plesk-import/snapshots/${snapshotId}`),
    enabled: snapshotId !== null,
  });

  if (!snapshotId) return null;
  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const report: PleskReadinessReport | null = detail.data.report;

  return (
    <Card>
      <CardHeader
        title="Migration-readiness report"
        description={
          report
            ? `Generated ${new Date(report.generatedAt).toLocaleString()} — flags mark subscriptions needing more than the standard migration procedure.`
            : 'This snapshot has no report (it failed before completing).'
        }
        actions={
          report && (
            <div className="flex gap-2">
              <a href={`/api/plesk-import/snapshots/${snapshotId}/export.csv`} download>
                <Button variant="secondary">CSV</Button>
              </a>
              <a href={`/api/plesk-import/snapshots/${snapshotId}/export.json`} download>
                <Button variant="secondary">JSON</Button>
              </a>
            </div>
          )
        }
      />
      {detail.data.warnings.length > 0 && (
        <div className="mx-5 mt-4 rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-300">
          <p className="font-medium">Collection gaps — verify these areas manually:</p>
          <ul className="mt-1 list-inside list-disc">
            {detail.data.warnings.map((w, i) => (
              <li key={i}>
                {w.scope}
                {w.subject ? ` (${w.subject})` : ''}: {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {report && (
        <>
          <dl className="grid grid-cols-2 gap-4 px-5 py-4 text-sm sm:grid-cols-4 lg:grid-cols-6">
            {(
              [
                ['Subscriptions', report.totals.subscriptions],
                ['Domains', report.totals.domains],
                ['Mailboxes', report.totals.mailboxes],
                ['Databases', report.totals.databases],
                ['DNS zones', report.totals.dnsZonesHosted],
                ['Certificates', report.totals.certificates],
                ['Cron jobs', report.totals.cronJobs ?? '—'],
                ['Disk', formatBytes(report.totals.diskUsageBytes)],
                ['Mail data', formatBytes(report.totals.mailUsageBytes)],
                ['DB data', formatBytes(report.totals.databaseUsageBytes)],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {label}
                </dt>
                <dd className="mt-0.5 font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-y border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3 font-medium">Subscription</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium">PHP</th>
                  <th className="px-5 py-3 text-right font-medium">Domains</th>
                  <th className="px-5 py-3 text-right font-medium">Mail</th>
                  <th className="px-5 py-3 text-right font-medium">DBs</th>
                  <th className="px-5 py-3 text-right font-medium">Cron</th>
                  <th className="px-5 py-3 font-medium">Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {report.subscriptions.map((sub) => (
                  <tr key={sub.pleskId}>
                    <td className="px-5 py-3 font-medium text-slate-800 dark:text-slate-100">
                      {sub.name}
                    </td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
                      {sub.customerName ?? sub.customerLogin ?? '—'}
                    </td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
                      {sub.planName ?? '—'}
                    </td>
                    <td className="px-5 py-3 tabular-nums text-slate-600 dark:text-slate-300">
                      {sub.phpVersions.join(', ') || '—'}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{sub.domains}</td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {sub.mailboxes}
                      {sub.mailUsageBytes !== null && (
                        <span className="text-xs text-slate-500"> ({formatBytes(sub.mailUsageBytes)})</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {Object.values(sub.databases).reduce((a, b) => a + b, 0)}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{sub.cronJobs ?? '—'}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {sub.flags.length === 0 ? (
                          <Badge tone="green">ready</Badge>
                        ) : (
                          sub.flags.map((f) => (
                            <span key={f.key} title={`${PLESK_FLAG_DEFS[f.key].description}\n${f.detail}`}>
                              <Badge tone={SEVERITY_TONE[f.severity]}>
                                {PLESK_FLAG_DEFS[f.key].label}
                              </Badge>
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

// ── Inventory browser ─────────────────────────────────────────────────

function InventoryCard({ snapshotId }: { snapshotId: string | null }) {
  const [kind, setKind] = useState<PleskItemKind | ''>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const items = useQuery({
    queryKey: ['plesk-import', 'items', snapshotId, kind, search, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (kind) params.set('kind', kind);
      if (search) params.set('q', search);
      return api.get<{ items: PleskInventoryItemDto[]; total: number }>(
        `/api/plesk-import/snapshots/${snapshotId}/items?${params}`,
      );
    },
    enabled: snapshotId !== null,
  });

  if (!snapshotId) return null;
  const total = items.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Card>
      <CardHeader
        title="Inventory browser"
        description="The raw recorded objects — what the account import will transform."
        actions={
          <div className="flex gap-2">
            <Select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as PleskItemKind | '');
                setPage(1);
              }}
            >
              <option value="">All kinds</option>
              {PLESK_ITEM_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </Select>
            <Input
              placeholder="Search names…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        }
      />
      {items.isPending ? (
        <Spinner />
      ) : items.error ? (
        <div className="p-5">
          <ErrorNote error={items.error} />
        </div>
      ) : items.data.items.length === 0 ? (
        <EmptyState title="Nothing recorded" description="No inventory items match this filter." />
      ) : (
        <>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.data.items.map((item) => (
              <li key={item.id} className="px-5 py-3">
                <details>
                  <summary className="flex cursor-pointer items-center gap-2 text-sm">
                    <Badge>{item.kind}</Badge>
                    <span className="font-medium text-slate-700 dark:text-slate-200">
                      {item.name}
                    </span>
                    <span className="text-xs text-slate-400">#{item.pleskId}</span>
                  </summary>
                  <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
                    {JSON.stringify(item.payload, null, 2)}
                  </pre>
                </details>
              </li>
            ))}
          </ul>
          {pages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm dark:border-slate-800">
              <span className="text-slate-500">
                Page {page} of {pages} · {total} items
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Prev
                </Button>
                <Button
                  variant="secondary"
                  disabled={page >= pages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────

export function PleskImportPage() {
  const status = usePleskStatus();
  const snapshots = useQuery({
    queryKey: ['plesk-import', 'snapshots'],
    queryFn: () => api.get<PleskSnapshotDto[]>('/api/plesk-import/snapshots'),
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Default the report to the newest snapshot that has one.
  useEffect(() => {
    if (selectedId || !snapshots.data) return;
    const withReport = snapshots.data.find(
      (s) => s.status === 'completed' || s.status === 'partial',
    );
    if (withReport) setSelectedId(withReport.id);
  }, [snapshots.data, selectedId]);

  if (status.error instanceof ApiClientError && status.error.status === 404) {
    return (
      <EmptyState
        title="Plesk Import module is disabled"
        description="An administrator can enable it under Modules."
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Plesk Import"
        description="Migrating off the legacy Plesk server: read-only inventory snapshots with a readiness report, then account import into panel entities. Nothing here ever changes Plesk."
      />
      <ConnectionCard />
      <SnapshotsCard selectedId={selectedId} onSelect={setSelectedId} />
      <ImportWizardCard />
      <ImportRunsCard />
      <ReportCard snapshotId={selectedId} />
      <InventoryCard snapshotId={selectedId} />
    </div>
  );
}
